// Block registry for the visual page builder.
// A page's content may contain an ordered `blocks` array:
//   content.blocks = [{ id, type, props }]
// When present, the public page renders these blocks; otherwise it falls back
// to its original hand-built design (non-destructive).

export const uid = () => Math.random().toString(36).slice(2, 10);

// Default props for each block type.
const DEFAULTS = {
  hero: () => ({
    eyebrow: "Traditional Karate · Chicagoland",
    headline: "Begin Your Journey",
    subtext:
      "Come join our class! A supportive, encouraging environment to learn the traditional art of Karate.",
    image:
      "https://images.unsplash.com/photo-1773017825177-25acaa271258?crop=entropy&cs=srgb&fm=jpg&q=85",
    primaryLabel: "Contact Us",
    primaryHref: "/contact",
    secondaryLabel: "About Us",
    secondaryHref: "/about",
  }),
  heading: () => ({ eyebrow: "", text: "Section heading", level: "h2", align: "left" }),
  text: () => ({
    html: "<p>Write something meaningful here. Click to edit this text.</p>",
    align: "left",
  }),
  image: () => ({ src: "", alt: "", caption: "", rounded: true }),
  button: () => ({ label: "Learn more", href: "/contact", variant: "primary", align: "left" }),
  gallery: () => ({ columns: 3, images: [] }),
  cards: () => ({
    columns: 3,
    items: [
      { title: "Feature one", body: "Describe this feature." },
      { title: "Feature two", body: "Describe this feature." },
      { title: "Feature three", body: "Describe this feature." },
    ],
  }),
  divider: () => ({}),
  spacer: () => ({ size: 48 }),
};

// Palette shown in the builder (order matters).
export const PALETTE = [
  { type: "hero", label: "Hero", desc: "Big banner with headline + buttons" },
  { type: "heading", label: "Heading", desc: "Section title" },
  { type: "text", label: "Text", desc: "Rich paragraph" },
  { type: "image", label: "Image", desc: "Single image + caption" },
  { type: "gallery", label: "Gallery", desc: "Grid of images" },
  { type: "cards", label: "Cards", desc: "Feature / info cards" },
  { type: "button", label: "Button", desc: "Call-to-action link" },
  { type: "divider", label: "Divider", desc: "Horizontal rule" },
  { type: "spacer", label: "Spacer", desc: "Vertical gap" },
];

export function newBlock(type) {
  const make = DEFAULTS[type] || (() => ({}));
  return { id: uid(), type, props: make() };
}

// Ready-made section presets — one click drops a themed group of blocks.
export const SECTION_PRESETS = [
  { key: "hero", label: "Hero banner", desc: "Headline + buttons" },
  { key: "textSection", label: "Text section", desc: "Heading + paragraph" },
  { key: "gallery", label: "Gallery", desc: "Heading + image grid" },
  { key: "features", label: "Feature cards", desc: "Heading + 3 cards" },
  { key: "testimonials", label: "Testimonials", desc: "Heading + quote cards" },
  { key: "cta", label: "Call to action", desc: "Heading + text + button" },
];

export function presetBlocks(key) {
  switch (key) {
    case "hero":
      return [newBlock("hero")];
    case "textSection":
      return [
        { id: uid(), type: "heading", props: { eyebrow: "", text: "Section heading", level: "h2", align: "center" } },
        { id: uid(), type: "text", props: { html: "<p>Write your content here.</p>", align: "center" } },
      ];
    case "gallery":
      return [
        { id: uid(), type: "heading", props: { eyebrow: "Gallery", text: "Moments from the dojo", level: "h2", align: "center" } },
        { id: uid(), type: "gallery", props: { columns: 3, images: [] } },
      ];
    case "features":
      return [
        { id: uid(), type: "heading", props: { eyebrow: "Why us", text: "What we offer", level: "h2", align: "center" } },
        newBlock("cards"),
      ];
    case "testimonials":
      return [
        { id: uid(), type: "heading", props: { eyebrow: "Testimonials", text: "What our members say", level: "h2", align: "center" } },
        {
          id: uid(),
          type: "cards",
          props: {
            columns: 3,
            items: [
              { title: "★★★★★", body: "\"Add a member quote here.\" — Member name" },
              { title: "★★★★★", body: "\"Add a member quote here.\" — Member name" },
              { title: "★★★★★", body: "\"Add a member quote here.\" — Member name" },
            ],
          },
        },
      ];
    case "cta":
      return [
        { id: uid(), type: "heading", props: { eyebrow: "", text: "Ready to begin?", level: "h2", align: "center" } },
        { id: uid(), type: "text", props: { html: "<p>Join our next class and start your journey.</p>", align: "center" } },
        { id: uid(), type: "button", props: { label: "Contact Us", href: "/contact", variant: "primary", align: "center" } },
      ];
    default:
      return [];
  }
}

// ─── Importing an existing page into editable blocks ──────────────────────
// Small helpers so each page importer stays readable.
const heading = (text, { eyebrow = "", level = "h2", align = "left" } = {}) => ({
  id: uid(), type: "heading", props: { eyebrow, text: text || "", level, align },
});
const text = (html, align = "left") => ({ id: uid(), type: "text", props: { html, align } });
const image = (src, { alt = "", caption = "", rounded = true } = {}) => ({
  id: uid(), type: "image", props: { src, alt, caption, rounded },
});
const cards = (items, columns = 3) => ({ id: uid(), type: "cards", props: { columns, items } });
const button = (label, href, { variant = "primary", align = "left" } = {}) => ({
  id: uid(), type: "button", props: { label, href, variant, align },
});
const divider = () => ({ id: uid(), type: "divider", props: {} });

const paragraphs = (body) =>
  String(body || "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${p}</p>`)
    .join("") || "<p></p>";

const instructorCards = (list) =>
  (list || []).map((ins) => ({
    title: ins.name || "",
    body: [ins.location, ins.phone].filter(Boolean).join(" · "),
  }));

// Build the editable block list from a page's existing hand-built content, so
// opening the builder shows the page the admin actually sees live — not a stub.
export function starterBlocks(slug, content = {}) {
  const c = content || {};
  const out = [];

  if (slug === "home") {
    return [
      {
        id: uid(),
        type: "hero",
        props: {
          eyebrow: c.tagline || "Traditional Karate · Chicagoland",
          headline: c.hero_headline || "Begin Your Journey",
          subtext:
            c.hero_sub ||
            c.hero_subtext ||
            "Come join our class! A supportive, encouraging environment to learn the traditional art of Karate.",
          image: c.hero_image || DEFAULTS.hero().image,
          primaryLabel: "Contact Us",
          primaryHref: "/contact",
          secondaryLabel: "About Us",
          secondaryHref: "/about",
        },
      },
      heading("Why train with us", { eyebrow: "Welcome", align: "center" }),
      text("<p>Edit this section to introduce your dojo.</p>", "center"),
    ];
  }

  if (slug === "about") {
    out.push(heading(c.heading || "About Us", { eyebrow: c.eyebrow || "About Us", level: "h1" }));
    if (c.image) out.push(image(c.image, { alt: "Yoshitaka Karate" }));
    if (c.body) out.push(text(paragraphs(c.body)));
    if (c.quote) out.push(text(`<blockquote>“${c.quote}”</blockquote>`));
    if ((c.values || []).length) {
      out.push(divider());
      out.push(cards(
        c.values.map((v) => ({ title: v.title || "", body: v.desc || "" })),
        Math.min(4, Math.max(2, c.values.length)),
      ));
    }
    if ((c.instructors || []).length) {
      out.push(heading(c.instructors_heading || "Instructors"));
      out.push(cards(instructorCards(c.instructors)));
    }
    return out;
  }

  if (slug === "programs") {
    out.push(heading(c.heading || "Programs", { eyebrow: c.eyebrow || "Our Classes", level: "h1" }));
    if (c.intro) out.push(text(paragraphs(c.intro)));
    if ((c.programs || []).length) {
      out.push(cards(
        c.programs.map((p) => ({ title: p.name || "", body: p.desc || "" })),
      ));
    }
    out.push(button("Contact Us", "/contact"));
    return out;
  }

  if (slug === "schedule") {
    out.push(heading(c.heading || "Weekly Schedule", { eyebrow: c.eyebrow || "Classes", level: "h1" }));
    if (c.intro) out.push(text(paragraphs(c.intro)));
    if ((c.classes || []).length) {
      out.push(cards(
        c.classes.map((k) => ({
          title: [k.day, k.class].filter(Boolean).join(" · "),
          body: k.time || "",
        })),
      ));
    }
    return out;
  }

  if (slug === "contact") {
    out.push(heading(c.heading || "Contact", { eyebrow: c.eyebrow || "Contact", level: "h1" }));
    if (c.intro) out.push(text(paragraphs(c.intro)));
    if ((c.locations || []).length) {
      out.push(heading("Our Locations", { level: "h2" }));
      out.push(cards(
        c.locations.map((l) => ({
          title: l.name || "",
          body: [l.address, l.phone].filter(Boolean).join(" · "),
        })),
      ));
    }
    const details = [c.email && `Email: ${c.email}`, c.hours && `Hours: ${c.hours}`].filter(Boolean);
    if (details.length) out.push(text(details.map((d) => `<p>${d}</p>`).join("")));
    return out;
  }

  if (slug === "news") {
    out.push(heading(c.heading || "News & Events", { eyebrow: c.eyebrow || "Dojo News", level: "h1" }));
    if ((c.posts || []).length) {
      out.push(cards(
        c.posts.map((p) => ({
          title: p.title || "",
          body: [p.date, p.body].filter(Boolean).join(" — "),
        })),
        2,
      ));
    }
    return out;
  }

  // Unknown page — fall back to a minimal editable stub.
  const title = c.title || (slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : "Page");
  return [
    heading(title, { level: "h1" }),
    text("<p>Start editing this page. Add blocks from the palette.</p>"),
  ];
}
